import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Factory } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Shamani Industries — Raj Shamani's Family Manufacturing Business";
const DESC =
  "What Shamani Industries does: the polymer additives manufacturer behind Raj Shamani's family business — products, markets, history and his role in the company.";
const URL = "https://rajshamani.life/shamani-industries";
const OG = OG_IMAGES.ventures;

const faq = [
  {
    q: "What is Shamani Industries?",
    a: "Shamani Industries is an Indian manufacturing company specialising in polymer additives and speciality chemical inputs used by plastics and packaging manufacturers.",
  },
  {
    q: "Is Shamani Industries Raj Shamani's company?",
    a: "Shamani Industries is Raj Shamani's family business. He joined it in his teens, took on sales and export responsibility, and helped expand its distribution before building his own media and consumer ventures.",
  },
  {
    q: "What does Shamani Industries manufacture?",
    a: "The company manufactures and distributes polymer additives and related industrial inputs supplied to plastic processors and packaging producers in India and export markets.",
  },
  {
    q: "Where is Shamani Industries based?",
    a: "The business operates out of India, serving domestic manufacturers alongside export customers across Asia, the Middle East and Africa.",
  },
  {
    q: "What is Raj Shamani's role at Shamani Industries today?",
    a: "Raj remains associated with the family business while focusing day-to-day on Figuring Out Media, House of X and his early-stage investments.",
  },
];

export const Route = createFileRoute("/shamani-industries")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Shamani Industries — Raj Shamani's family manufacturing business", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Organization",
            "@id": `${URL}#shamani-industries`,
            name: "Shamani Industries",
            description:
              "Indian manufacturer of polymer additives and speciality industrial inputs; the Shamani family business.",
            industry: "Chemical Manufacturing",
            areaServed: "IN",
            url: URL,
            employee: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life/about" },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ShamaniIndustries,
});

const facts = [
  ["Company", "Shamani Industries"],
  ["Sector", "Polymer additives & speciality chemicals"],
  ["Type", "Family-owned manufacturing business"],
  ["Markets", "India plus export markets in Asia, Middle East, Africa"],
  ["Association", "Raj Shamani — family business, early sales & export role"],
];

function ShamaniIndustries() {
  return (
    <div>
      <PageBreadcrumb current="Shamani Industries" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Factory size={14} /> Manufacturing
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Shamani Industries — <span className="text-gradient-gold">the family business</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Long before the podcast, the investing or the consumer brands, there
            was <strong>Shamani Industries</strong> — the family manufacturing
            business where Raj Shamani learned how commerce actually works.
            It manufactures and distributes polymer additives and related
            industrial inputs for plastics and packaging manufacturers across
            India and export markets.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Company at a glance</h2>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {facts.map(([k, v]) => (
            <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 px-6 py-4">
              <dt className="text-sm text-muted-foreground">{k}</dt>
              <dd className="text-sm sm:text-right sm:max-w-md">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20 grid md:grid-cols-2 gap-6">
        <div className="p-8 rounded-xl bg-card border border-border">
          <h2 className="text-2xl font-bold mb-3 text-gradient-gold">What Raj learned there</h2>
          <p className="text-muted-foreground leading-relaxed">
            Selling industrial inputs to hard-nosed factory buyers taught margin
            discipline, cash-flow reality and the difference between a customer
            and a relationship — the same lens he now applies to consumer brands
            and early-stage bets.
          </p>
        </div>
        <div className="p-8 rounded-xl bg-card border border-border">
          <h2 className="text-2xl font-bold mb-3 text-gradient-gold">Where it sits today</h2>
          <p className="text-muted-foreground leading-relaxed">
            Shamani Industries continues as the family's manufacturing arm while
            Raj focuses on Figuring Out Media, House of X and his investment
            portfolio.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-20 space-y-12">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            What does Shamani Industries do?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Shamani Industries operates in polymer additives and speciality
            industrial inputs — the unglamorous chemistry that sits inside
            almost every plastic product you touch. Additives are what turn a
            base polymer into a usable material: masterbatches that colour it,
            stabilisers that stop it degrading under sunlight, fillers that cut
            cost without cutting strength, and processing aids that let a
            factory run its extruders faster without scrap.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The customers are plastic processors, packaging producers and
            moulders — businesses that buy on specification, sample approval and
            consistency rather than on branding. In that market a supplier wins
            by holding quality batch after batch and by being reachable when a
            production line stops at midnight. It is a slow, relationship-heavy
            business, and it is the environment Raj Shamani grew up inside.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Raj Shamani and the family business
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Raj joined the family business as a teenager, well before the
            podcast or the investing. His role was commercial rather than
            technical: finding buyers, handling export enquiries, negotiating
            terms and travelling to trade fairs where he was routinely the
            youngest person in the hall by two decades. Selling an industrial
            input to a procurement head who has bought the same product for
            twenty years is a hard first job, and it is the origin story he
            returns to most often on Figuring Out.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            That work funded and shaped everything after it. The habit of
            speaking to strangers for a living became the interview style. The
            habit of watching cash conversion cycles became the lens he uses on
            D2C brands. And the frustration of selling a commodity with no
            pricing power is exactly why he later chose businesses with brand
            and audience leverage instead.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Markets, exports and how the business operates
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            The company serves domestic Indian manufacturers alongside export
            customers across Asia, the Middle East and Africa. Exporting a
            speciality chemical input is a logistics business as much as a
            chemical one: documentation, hazardous-goods classification,
            container scheduling and currency exposure all sit between a quote
            and a paid invoice. Margins are thin enough that a shipping delay
            can erase a deal's profit entirely.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Structurally it is a classic Indian family manufacturing SME —
            promoter-run, reinvestment-funded, without outside capital. That
            model trades growth speed for control and survivability, which is
            precisely the trade-off Raj interrogates when he interviews founders
            who took venture money instead.
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Shamani Industries vs Raj Shamani's other ventures
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            It is worth separating the three things people conflate. Shamani
            Industries is the family manufacturing business, B2B and
            decades-old. <strong>Figuring Out Media</strong> is the podcast and
            content company Raj founded, which monetises attention through
            sponsorship and distribution. <strong>House of X</strong> is the
            consumer brand house, which builds and scales D2C products directly
            to Indian customers. On top of those sits an angel portfolio of
            early-stage Indian startups.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Today Raj's day-to-day sits with media, brands and investing, while
            the manufacturing business continues as the family's operating arm.
            The full picture of what he owns and backs is on the{" "}
            <Link to="/ventures" className="text-gold hover:underline">
              ventures page
            </Link>
            .
          </p>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Why the manufacturing years still matter
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Most creators who talk about business have never issued an invoice
            with sixty-day credit terms and then chased it. The Shamani
            Industries years are what stop the advice from floating: margin
            discipline, the difference between revenue and collected cash, the
            fact that a customer relationship is worth more than any single
            order, and the knowledge that distribution — not the product — is
            usually the constraint. Those are the ideas that recur across{" "}
            <Link to="/raj-shamani-books" className="text-gold hover:underline">
              Build Don't Talk
            </Link>{" "}
            and across the podcast archive, and they came from a factory floor
            rather than a business school.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">

        <h2 className="text-3xl md:text-4xl font-bold mb-10">
          Shamani Industries — frequently asked questions
        </h2>
        <div className="space-y-8">
          {faq.map((f) => (
            <div key={f.q} className="pb-8 border-b border-border/50 last:border-0">
              <h3 className="text-lg font-bold mb-2">{f.q}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap gap-4 text-sm">
          <Link to="/ventures" className="inline-flex items-center gap-1 text-gold hover:underline">
            All ventures & investments <ArrowRight size={14} />
          </Link>
          <Link to="/about" className="text-gold hover:underline">About Raj Shamani</Link>
          <Link to="/raj-shamani-books" className="text-gold hover:underline">Raj Shamani books</Link>
          <Link to="/raj-shamani-podcast" className="text-gold hover:underline">Raj Shamani podcast</Link>
        </div>
      </section>
    <AionCta />
    <RelatedKeywords currentPath="/shamani-industries" />
    <RelatedPages pages={["aion", "ventures", "about", "podcast", "book", "faq", "contact"]} />
      </div>
  );
}
